import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, Search, Users, BarChart3 } from "lucide-react";

export const ROLE_LABEL = {
  admin: "Admin", platinum: "Platinum", team_leader: "Team Leader", leader: "Leader",
  partner: "Partner", oversight: "ATL Oversight",
};
export const TEAM_USERS_ROLES = ["admin", "platinum", "team_leader", "leader"];

export default function TeamUsersCard() {
  const { user, isViewAs, startViewAs } = useAuth();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/team-users").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list || [];
    return (list || []).filter((u) =>
      [u.name, u.upline_team, ROLE_LABEL[u.role], u.nearest_upline_leader_name]
        .some((v) => (v || "").toLowerCase().includes(s)));
  }, [list, q]);

  const viewAs = async (u) => {
    if (await startViewAs(u.id)) window.location.href = "/";
    else toast.error(`Unable to View As ${u.name}`);
  };

  return (
    <Card className="p-5" data-testid="team-users-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0A1F44] text-[#C9A961] flex items-center justify-center shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-lg text-[#0A1F44]">Team Users</h2>
            <p className="text-xs text-slate-500" data-testid="team-users-subtitle">
              {user?.role === "admin" ? "Everyone in Team Smith" : "People in your branch"} ·{" "}
              <span data-testid="team-users-count">{list ? list.length : "…"}</span> users · tap the eye for a read-only preview
            </p>
            <Link to="/milestones" data-testid="team-users-report-link"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#0A1F44] hover:text-[#8A6D1F] mt-1">
              <BarChart3 className="w-3.5 h-3.5" /> Milestone & qualification report
            </Link>
          </div>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, team or role"
                 className="pl-9 h-9 text-sm" data-testid="team-users-search" />
        </div>
      </div>
      {/* Shows about four rows; the rest scroll inside the card so the page doesn't grow. Search covers the full list. */}
      <div className="max-h-[26rem] sm:max-h-[20rem] overflow-y-auto overscroll-contain -mx-1 px-1 divide-y divide-slate-100" data-testid="team-users-list"
           tabIndex={0} aria-label="Team users list">
        {list === null && <div className="text-sm text-slate-400 py-4">Loading…</div>}
        {list && filtered.length === 0 && (
          <div className="text-sm text-slate-500 py-4" data-testid="team-users-empty">
            {list.length ? "No one matches your search." : "No one in your branch yet."}
          </div>
        )}
        {filtered.map((u) => <TeamUserRow key={u.id} u={u} canViewAs={!isViewAs} onViewAs={viewAs} />)}
      </div>
    </Card>
  );
}

function TeamUserRow({ u, canViewAs, onViewAs }) {
  const bc = u.bootcamp || {};
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`team-user-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/people/${u.id}`} data-testid={`team-user-profile-${u.id}`}
                className="text-sm font-medium text-slate-900 truncate hover:text-[#8A6D1F] hover:underline">{u.name}</Link>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0A1F44]/5 text-[#0A1F44]">
            {ROLE_LABEL[u.role] || u.role}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">
          {u.upline_team || "No team"}{u.nearest_upline_leader_name ? ` · Upline: ${u.nearest_upline_leader_name}` : ""}
        </div>
        <div className="flex items-center gap-x-2 gap-y-1 mt-1.5 flex-wrap" data-testid={`team-user-bootcamp-${u.id}`}>
          <div className="h-1.5 w-20 rounded-full bg-slate-100 overflow-hidden shrink-0">
            <div className="h-full bg-[#C9A961] transition-[width] duration-500" style={{ width: `${bc.pct || 0}%` }} />
          </div>
          <span className="text-[11px] text-slate-600 tabular-nums whitespace-nowrap">
            Bootcamp {bc.completed ?? 0}/{bc.total ?? 45} · {bc.pct ?? 0}%
          </span>
          <span className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap">
            Milestones {bc.milestones_current ?? 0} current
          </span>
        </div>
      </div>
      {canViewAs && (
        <button onClick={() => onViewAs(u)} data-testid={`team-user-view-as-${u.id}`} title={`View as ${u.name} (read only)`}
                className="w-9 h-9 shrink-0 rounded-full border border-slate-200 text-[#0A1F44] flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-colors">
          <Eye className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
